import { APP_ROUTES } from '@routes/routes.constants';
import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { InlineFeedbackProps } from './InlineFeedback.types';

import {
  FEEDBACK_CONFIGS,
  isErrorFeedbackType,
} from '../feedback-content/FeedbackContent.utils';

/**
 * Inline feedback component for use INSIDE layouts (header/nav still visible).
 *
 * Uses FEEDBACK_CONFIGS to look up illustration, title, and message.
 * Supports both error types (bad-request, forbidden, etc.) and content
 * types (data-error, so-empty, no-search-results).
 *
 * - Error types: showGoHome defaults to true
 * - Content types: showGoHome defaults to false
 *
 * For errors OUTSIDE layouts (no header/nav), use ErrorPage instead.
 *
 * Used by:
 * - Route guards (PermissionGuard, FeatureFlagGuard, VisitorGuard, etc.)
 * - MFE page wrappers (ReservationsRouterPage, RecordsRouterPage, etc.)
 * - Feature content areas (UsersContent, etc.)
 */
const InlineFeedback = React.memo(
  ({
    type,
    showGoHome,
    message,
  }: Readonly<InlineFeedbackProps>): React.ReactElement => {
    const navigate = useNavigate();
    const config = FEEDBACK_CONFIGS[type];

    const shouldShowGoHome = showGoHome ?? isErrorFeedbackType(type);

    const handleGoHome = React.useCallback((): void => {
      navigate(APP_ROUTES.APP_INITIALIZER);
    }, [navigate]);

    return (
      <FeedbackContent
        title={config.title}
        titleSize="heading-01"
        message={message ?? config.message}
        illustration={<config.Illustration />}
        primaryActionLabel="Go to Homepage"
        onPrimaryAction={shouldShowGoHome ? handleGoHome : undefined}
        showPrimaryAction={shouldShowGoHome}
        showSecondaryAction={false}
      />
    );
  }
);

InlineFeedback.displayName = 'InlineFeedback';

export { InlineFeedback };
